/**
 * THM Wishlist
 * -----------------------------------------------------------------------
 * Client-side wishlist for Tech House Market (Focal theme).
 *
 * - Works immediately for everyone via localStorage, no backend needed.
 * - If a customer is logged in AND `window.THMWishlistSyncEndpoint` is
 *   configured (see backend-optional/README.md), the wishlist also gets
 *   merged with a copy tied to their customer account, so it carries
 *   over across devices/browsers.
 * - Without that endpoint, everything keeps working, it just stays
 *   local to the browser (as most simple wishlist apps do by default).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'thm-wishlist';

  function readLocal() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  }

  function writeLocal(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      // localStorage unavailable (e.g. private browsing) - fail silently
    }
  }

  function dispatchUpdated(items) {
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: items } }));
  }

  var Wishlist = {
    STORAGE_KEY: STORAGE_KEY,

    getItems: function () {
      return readLocal();
    },

    has: function (productId) {
      productId = String(productId);
      return readLocal().some(function (item) {
        return String(item.id) === productId;
      });
    },

    add: function (item) {
      var items = readLocal();
      var alreadyIn = items.some(function (existing) {
        return String(existing.id) === String(item.id);
      });

      if (!alreadyIn) {
        items.push(item);
        writeLocal(items);
        dispatchUpdated(items);
        Wishlist.pushToAccount(items);
      }

      return items;
    },

    remove: function (productId) {
      var items = readLocal().filter(function (item) {
        return String(item.id) !== String(productId);
      });

      writeLocal(items);
      dispatchUpdated(items);
      Wishlist.pushToAccount(items);

      return items;
    },

    toggle: function (item) {
      return Wishlist.has(item.id) ? Wishlist.remove(item.id) : Wishlist.add(item);
    },

    clear: function () {
      writeLocal([]);
      dispatchUpdated([]);
      Wishlist.pushToAccount([]);
    },

    /* ---------------------------------------------------------------
       Optional account sync, see backend-optional/README.md.
       Does nothing as long as THMWishlistSyncEndpoint isn't set.
    --------------------------------------------------------------- */
    pushToAccount: function (items) {
      if (!window.THMCustomerId || !window.THMWishlistSyncEndpoint) return;

      fetch(window.THMWishlistSyncEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: window.THMCustomerId,
          customer_email: window.THMCustomerEmail,
          items: items
        })
      }).catch(function () {
        // no problem, stays local-only for now
      });
    },

    pullFromAccountAndMerge: function () {
      if (!window.THMCustomerId || !window.THMWishlistSyncEndpoint) return;

      var endpoint = window.THMWishlistSyncEndpoint + '?customer_id=' + encodeURIComponent(window.THMCustomerId);

      fetch(endpoint)
        .then(function (response) {
          return response.ok ? response.json() : { items: [] };
        })
        .then(function (data) {
          var remoteItems = (data && data.items) || [];
          var merged = readLocal().slice();

          remoteItems.forEach(function (remoteItem) {
            var alreadyIn = merged.some(function (item) {
              return String(item.id) === String(remoteItem.id);
            });
            if (!alreadyIn) merged.push(remoteItem);
          });

          writeLocal(merged);
          dispatchUpdated(merged);

          // write the merged list back, so items that only existed
          // locally also end up on the account
          Wishlist.pushToAccount(merged);
        })
        .catch(function () {
          // sync endpoint unreachable - just stay local-only
        });
    }
  };

  window.THMWishlist = Wishlist;

  /* -------------------------------------------------------------------
     <wishlist-button> custom element, used in product-item.liquid
     and snippets/product-form.liquid
  ------------------------------------------------------------------- */
  if (!window.customElements.get('wishlist-button')) {
    class WishlistButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.onClick = this.onClick.bind(this);
        this.refresh = this.refresh.bind(this);

        this.addEventListener('click', this.onClick);
        document.addEventListener('wishlist:updated', this.refresh);

        this.refresh();
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:updated', this.refresh);
      }

      getItem() {
        return {
          id: this.dataset.productId,
          handle: this.dataset.productHandle,
          url: this.dataset.productUrl,
          title: this.dataset.productTitle,
          image: this.dataset.productImage,
          price: this.dataset.productPrice,
          variantId: this.dataset.variantId
        };
      }

      onClick(event) {
        event.preventDefault();
        Wishlist.toggle(this.getItem());
      }

      refresh() {
        var active = Wishlist.has(this.dataset.productId);
        this.classList.toggle('is-active', active);

        if (this.button) {
          this.button.setAttribute('aria-pressed', String(active));
          this.button.setAttribute(
            'aria-label',
            active ? 'Remove from wishlist' : 'Add to wishlist'
          );
        }
      }
    }

    window.customElements.define('wishlist-button', WishlistButton);
  }

  /* Keeps any header counters in sync, e.g.
     <span data-wishlist-count></span> */
  function updateCounters(items) {
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = items.length;
      el.hidden = items.length === 0;
    });
  }

  document.addEventListener('wishlist:updated', function (event) {
    updateCounters(event.detail.items);
  });

  document.addEventListener('DOMContentLoaded', function () {
    updateCounters(readLocal());
    Wishlist.pullFromAccountAndMerge();
  });
})();